import type { MatterDTO } from "../../../../api/types/matter";
import { getPetitionerName } from "../../../../utils/matterDisplay";
import { getCaseFilterBucket, type CasesFilter } from "../../lib/caseStatus";

export type CasesSortBy = "" | "created_at" | "status" | "visa";
export type CasesSortDir = "asc" | "desc";
export type CasesSort = { by: CasesSortBy; dir: CasesSortDir };

export const DEFAULT_CASES_SORT: CasesSort = { by: "", dir: "desc" };

export const SORT_OPTIONS: ReadonlyArray<{
  key: string;
  label: string;
  sort: CasesSort;
}> = [
  { key: "default", label: "Default", sort: { by: "", dir: "desc" } },
  {
    key: "created_desc",
    label: "Date Created (Newest)",
    sort: { by: "created_at", dir: "desc" }
  },
  {
    key: "created_asc",
    label: "Date Created (Oldest)",
    sort: { by: "created_at", dir: "asc" }
  },
  {
    key: "status_asc",
    label: "Status (A-Z)",
    sort: { by: "status", dir: "asc" }
  },
  { key: "visa_asc", label: "Visa (A-Z)", sort: { by: "visa", dir: "asc" } }
];

export const isSameSort = (a: CasesSort, b: CasesSort): boolean =>
  a.by === b.by && (a.by === "created_at" ? a.dir === b.dir : true);

export const sortLabel = (sort: CasesSort): string =>
  SORT_OPTIONS.find((o) => isSameSort(o.sort, sort))?.label ?? "Default";

const createdAtMs = (m: MatterDTO): number => {
  const ms = Date.parse((m.case?.created_at ?? "").toString());
  return Number.isFinite(ms) ? ms : 0;
};

const compareText = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: "base" });

/** Sort then filter, exactly as the legacy cases table did. */
export const visibleCases = (
  matters: MatterDTO[],
  filter: CasesFilter,
  sort: CasesSort
): MatterDTO[] => {
  const by = sort.by || "created_at";
  const dir = by === "created_at" ? sort.dir : "asc";
  const sign = dir === "asc" ? 1 : -1;
  const sorted = [...matters].sort((a, b) => {
    if (by === "created_at") return (createdAtMs(a) - createdAtMs(b)) * sign;
    if (by === "status") {
      return (
        compareText(
          (a.case?.status ?? "").toString().trim(),
          (b.case?.status ?? "").toString().trim()
        ) * sign
      );
    }
    return (
      compareText(
        (a.case?.visa ?? "").toString().trim(),
        (b.case?.visa ?? "").toString().trim()
      ) * sign
    );
  });
  if (filter === "all") return sorted;
  return sorted.filter(
    (m) => getCaseFilterBucket(m.case?.status ?? null) === filter
  );
};

/** Case-insensitive match on visa, status or petitioner name. */
export const searchCases = (
  matters: MatterDTO[],
  query: string
): MatterDTO[] => {
  const q = query.trim().toLowerCase();
  if (!q) return matters;
  return matters.filter((m) => {
    const haystack = [m.case?.visa, m.case?.status, getPetitionerName(m)]
      .map((v) => (v ?? "").toString().toLowerCase())
      .join(" ");
    return haystack.includes(q);
  });
};

/** RFE flag and workflow type off the matter's loosely-typed individual. */
export const matterWorkflowFlags = (
  matter: MatterDTO
): { isRfe: boolean; workflowType: string | null } => {
  const ind = (matter.individual ?? {}) as Record<string, unknown>;
  const isRfe = Boolean(ind.is_rfe ?? ind.isRfe);
  const workflowType =
    typeof ind.workflow_type === "string" ? ind.workflow_type : null;
  return { isRfe, workflowType };
};
